import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { T, useTranslate } from '@tolgee/react';
import { OrderInterface } from '../OrdersTable/interfaces/Order.interface';
import { SypacIcon, SypacText } from '@sypac/component-library-react';
import {
	DocumentsDTO,
	DocumentUploadDTO,
	FileService,
	LoadDetailsDTO,
} from '../../services/file.service';
import DocumentsViewer from '../DocumentsViewer/DocumentsViewer';
import SimpleFile from '../../assets/SimpleFile';
import { MutatingDots } from 'react-loader-spinner';
import { OrderTarget } from '../OrdersTable/interfaces/OrderStatus.interface';
import { OrderDetailsContext } from '../../context/OrderDetailsContext/order-details.context';
import { getFileType } from '../../utils/files.util';
import { FileType, PageProps } from '../../constants';
import { Document, Page } from 'react-pdf';
import { cn } from '../../lib/utils';
import dayjs from 'dayjs';

interface DocumentUploadOrigin {
	truck: DocumentsDTO;
	upload?: DocumentUploadDTO;
}

const getFileName = (url: string) => {
	const path = url.split('?')[0];
	return decodeURIComponent(path.substring(path.lastIndexOf('/') + 1));
};

const matchUpload = (
	details: LoadDetailsDTO,
	url: string,
	index: number,
): DocumentUploadDTO | undefined => {
	const fileName = getFileName(url);
	const byName = details.uploads?.find(
		(upload) => upload.name === url || upload.name === fileName,
	);
	if (byName) return byName;

	return details.uploads?.length === details.documents.length
		? details.uploads[index]
		: undefined;
};

interface DocumentsTabProps {
	selectedOrder: OrderInterface | undefined;
	target: string;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
	selectedOrder,
	target,
}) => {
	const { t } = useTranslate();
	const { carrierInvoice, producerInvoice } = useContext(OrderDetailsContext);
	const [openDocView, setOpenDocView] = useState<boolean>(false);
	const [url, setUrl] = useState<string>();
	const [showBlur, setShowBlur] = useState<string>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [docs, setDocs] = useState<DocumentsDTO[]>();
	const [showPdfBlur, setShowPdfBlur] = useState<string | undefined>(undefined);

	const hasUndefinedLoadDetails = selectedOrder?.deliveryIncluded
		? docs?.some((doc) => doc.loadDetails === undefined)
		: docs?.some((doc) => doc.producerLoadDetails === undefined);

	const documentUploads = useMemo(() => {
		const uploads = new Map<string, DocumentUploadOrigin>();
		docs?.forEach((truck) =>
			[truck.loadDetails, truck.producerLoadDetails, truck.unloadDetails]
				.filter(Boolean)
				.forEach((details) =>
					details.documents?.forEach((url, index) => {
						if (!uploads.has(url)) {
							uploads.set(url, {
								truck,
								upload: matchUpload(details, url, index),
							});
						}
					}),
				),
		);
		return uploads;
	}, [docs]);

	const paymentDocs = useMemo(
		() => [
			...(carrierInvoice?.fullInvoiceUrls ?? []),
			...(producerInvoice?.fullInvoiceUrls ?? []),
		],
		[carrierInvoice, producerInvoice],
	);

	const getDocsCallback = useCallback(async () => {
		setIsLoading(true);
		try {
			if (selectedOrder?.id && selectedOrder?.target) {
				const { data } = await FileService.getFiles(
					selectedOrder.id!,
					selectedOrder.target!,
				);
				setDocs(data);
			}
		} catch (e) {
		} finally {
			setTimeout(() => {
				setIsLoading(false);
			}, 500);
		}
	}, [selectedOrder?.id, selectedOrder?.target]);

	useEffect(() => {
		getDocsCallback().then(() => {});
	}, [getDocsCallback]);

	const openFile = (url: string) => {
		setUrl(url);
		setOpenDocView(true);
	};

	const handleBoxToggle = (key?: string) => {
		if (key !== showBlur) {
			setShowBlur(key);
		}
	};

	const handlePdfBoxToggle = (url?: string) => {
		setShowPdfBlur(url);
	};

	const findDocumentOrigin = (dataArray: DocumentsDTO[], search: string) => {
		for (const item of dataArray) {
			if (
				item.loadDetails?.documents?.includes(search) ||
				(selectedOrder?.target === OrderTarget.producer &&
					item.producerLoadDetails?.documents?.includes(search))
			) {
				return 'Pick-up document';
			} else if (item.unloadDetails?.documents?.includes(search)) {
				return 'Proof of delivery document';
			}
		}
		return undefined;
	};

	const getUploaderName = (truck: DocumentsDTO, upload: DocumentUploadDTO) => {
		const role = t(
			`documentsViewer.role.${upload.uploadedByRole}`,
			upload.uploadedByRole,
		);
		const name =
			upload.uploadedByRole === 'driver' &&
			truck.driver?.uid === upload.uploadedBy
				? truck.driver.driverFullName
				: undefined;
		return name ? `${name} (${role})` : role;
	};

	const getViewerDetails = (search: string) => {
		const origin = documentUploads.get(search);
		return {
			text: findDocumentOrigin(docs!, search)!,
			updatedAt: origin?.truck.updatedAt ?? docs?.[0]?.updatedAt,
			uploadedAt: origin?.upload?.uploadedAt,
			uploadedBy: origin?.upload
				? getUploaderName(origin.truck, origin.upload)
				: undefined,
		};
	};

	const ImageCard = ({ imageUrl }: { imageUrl: string }) => {
		const origin = documentUploads.get(imageUrl);

		return (
			<div className="flex flex-col gap-1.5 w-[144px]">
				<div
					style={{
						backgroundImage: `url(${imageUrl})`,
					}}
					className={`relative flex flex-none bg-no-repeat bg-cover bg-center rounded-[12px] border border-solid border-gray-10 cursor-pointer w-[144px] h-[151px] ${
						showBlur === imageUrl ? 'hover:border-kimberly' : ''
					}`}
					onClick={() => openFile(imageUrl)}
					onMouseEnter={() => handleBoxToggle(imageUrl)}
					onMouseLeave={() => handleBoxToggle(undefined)}
					role="img"
				>
					{showBlur === imageUrl && (
						<div className="bg-black-50 absolute rounded-lg backdrop-blur-sm h-full w-full flex justify-center items-center">
							<SypacIcon
								size="custom"
								width="32px"
								height="32px"
								icon-name="Magnifer"
								className="text-white"
							/>
						</div>
					)}
				</div>
				{origin?.upload ? (
					<div className="flex flex-col gap-0.5">
						<SypacText variant="body-regular-medium">
							<p
								className="text-xs text-gray-80 truncate"
								title={getUploaderName(origin.truck, origin.upload)}
							>
								{getUploaderName(origin.truck, origin.upload)}
							</p>
						</SypacText>
						<SypacText variant="body-regular-medium">
							<p className="text-xs text-gray-40">
								{dayjs(origin.upload.uploadedAt).format('DD MMM, YYYY HH:mm')}
							</p>
						</SypacText>
					</div>
				) : null}
			</div>
		);
	};

	const renderDocuments = (docProperty: string) =>
		docs?.map((doc, index) => (
			<React.Fragment key={index}>
				{/*@ts-ignore*/}
				{doc[docProperty]?.documents?.map((url, idx) => (
					<ImageCard key={idx} imageUrl={url} />
				))}
			</React.Fragment>
		));

	const renderNoDocuments = () => (
		<div className="flex flex-col justify-center items-center gap-14 mt-12">
			<span className="flex scale-[3.35]">
				<SimpleFile color="#454545" />
			</span>
			<div className="flex flex-col gap-2">
				<SypacText variant="body-regular-medium">
					<p className="text-xl text-gray-80">
						<T keyName="documentsTab.noDocumentsUploaded">
							No documents uploaded yet
						</T>
					</p>
				</SypacText>
			</div>
		</div>
	);

	return (
		<>
			<div>
				<div className="flex flex-col gap-6 px-10 pb-3.5">
					<SypacText variant="body-regular-medium">
						<p className="text-gray-40">
							<T keyName="documentsTab.proofOfLoading">Proof of loading</T>
						</p>
					</SypacText>
					{isLoading ? (
						<div className="flex w-full h-full items-center justify-center">
							<MutatingDots
								height="166"
								width="100"
								color="#7693F4"
								secondaryColor="#494C83"
								radius="12.5"
								ariaLabel="mutating-dots-loading"
								visible
							/>
						</div>
					) : (
						<div
							className={`gap-3 mb-4 ${
								hasUndefinedLoadDetails ? 'justify-center' : ''
							} ${
								(docs ?? []).filter(
									(item) =>
										Object.keys(item?.loadDetails ?? {}).length > 0 ||
										Object.keys(item?.producerLoadDetails ?? {}).length > 0,
								).length > 4
									? 'grid grid-cols-4'
									: 'flex'
							}`}
						>
							{docs?.some((item) => item.loadDetails !== undefined)
								? renderDocuments('loadDetails')
								: docs?.some((item) => item.producerLoadDetails !== undefined)
								? renderDocuments('producerLoadDetails')
								: renderNoDocuments()}
						</div>
					)}
				</div>

				{selectedOrder?.target !== OrderTarget.producer ? (
					<>
						<div className="border-0 border-solid border-t-[1px] border-gray-10" />
						<div className="flex flex-col gap-6 px-10 pt-8">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="documentsTab.proofOfDelivery">
										Proof of delivery
									</T>
								</p>
							</SypacText>
							{isLoading ? (
								<div className="flex w-full h-full items-center justify-center">
									<MutatingDots
										height="166"
										width="100"
										color="#7693F4"
										secondaryColor="#494C83"
										radius="12.5"
										ariaLabel="mutating-dots-loading"
										visible
									/>
								</div>
							) : (
								<div
									className={`gap-3 mb-4 ${
										docs?.some((item) => item.unloadDetails === undefined)
											? 'justify-center'
											: ''
									} ${
										(docs ?? []).filter(
											(item) =>
												Object.keys(item?.unloadDetails ?? {}).length > 0,
										).length > 4
											? 'grid grid-cols-4'
											: 'flex'
									}`}
								>
									{docs?.some((item) => item.unloadDetails !== undefined) ? (
										docs?.map((doc, index) => (
											<React.Fragment key={index}>
												{doc?.unloadDetails?.documents?.map((url, idx) => (
													<ImageCard key={idx} imageUrl={url} />
												))}
											</React.Fragment>
										))
									) : (
										<div className="flex flex-col justify-center items-center gap-14 mt-12">
											<span className="flex scale-[3.35]">
												<SimpleFile color="#454545" />
											</span>

											<div className="flex flex-col gap-2">
												<SypacText variant="body-regular-medium">
													<p className="text-xl text-gray-80">
														<T keyName="documentsTab.noDocumentsUploaded">
															No documents uploaded yet
														</T>
													</p>
												</SypacText>
											</div>
										</div>
									)}
								</div>
							)}
						</div>
					</>
				) : null}

				<>
					<div className="border-0 border-solid border-t-[1px] border-gray-10" />
					<div className="flex flex-col gap-6 px-10 pt-8">
						<SypacText variant="body-regular-medium">
							<p className="text-gray-40">
								<T keyName="documentsTab.paymentDocuments">Payment documents</T>
							</p>
						</SypacText>

						{isLoading ? (
							<div className="flex w-full h-full items-center justify-center">
								<MutatingDots
									height="166"
									width="100"
									color="#7693F4"
									secondaryColor="#494C83"
									radius="12.5"
									ariaLabel="mutating-dots-loading"
									visible
								/>
							</div>
						) : (
							<div
								className={cn(
									'gap-3 mb-4',
									paymentDocs.length === 0 ? 'justify-center' : '',
									paymentDocs.length > 4 ? 'grid grid-cols-4' : 'flex',
								)}
							>
								{paymentDocs.length > 0
									? paymentDocs.map((imageUrl, index) => {
											const fileType = getFileType(imageUrl) as FileType;

											if ([FileType.JPG, FileType.PNG].includes(fileType)) {
												return (
													<div
														key={`image_${index}`}
														style={{ backgroundImage: `url(${imageUrl})` }}
														className={`relative flex flex-none bg-no-repeat bg-cover bg-center rounded-[12px] border border-solid border-gray-10 cursor-pointer w-[144px] h-[151px] ${
															showBlur === imageUrl
																? 'hover:border-kimberly'
																: ''
														}`}
														onClick={() => openFile(imageUrl)}
														onMouseEnter={() => handleBoxToggle(imageUrl)}
														onMouseLeave={() => handleBoxToggle(undefined)}
														role="img"
													>
														{showBlur === imageUrl && (
															<div className="bg-black-50 absolute rounded-lg backdrop-blur-sm h-full w-full flex justify-center items-center">
																<SypacIcon
																	size="custom"
																	width="32px"
																	height="32px"
																	icon-name="Magnifer"
																	className="text-white"
																/>
															</div>
														)}
													</div>
												);
											}

											if (fileType === FileType.PDF) {
												return (
													<div
														key={`pdf_${index}`}
														className="relative w-[144px] h-[151px] flex justify-center items-center border border-solid border-gray-10 rounded-lg overflow-hidden cursor-pointer"
														onClick={() => openFile(imageUrl)}
														onMouseEnter={() => handlePdfBoxToggle(imageUrl)}
														onMouseLeave={() => handlePdfBoxToggle(undefined)}
													>
														<Document file={imageUrl} renderMode="canvas">
															<Page
																pageNumber={1}
																{...PageProps}
																width={144}
																height={151}
																children={null}
															/>
														</Document>
														{showPdfBlur === imageUrl && (
															<div className="bg-black-50 absolute rounded-lg backdrop-blur-sm h-full w-full flex justify-center items-center">
																<SypacIcon
																	size="custom"
																	width="32px"
																	height="32px"
																	icon-name="Magnifer"
																	className="text-white"
																/>
															</div>
														)}
													</div>
												);
											}

											return null;
									  })
									: renderNoDocuments()}
							</div>
						)}
					</div>
				</>
			</div>
			{openDocView ? (
				<DocumentsViewer
					url={url!}
					isPdf={getFileType(url!) === FileType.PDF}
					isOpen={openDocView}
					onClose={() => setOpenDocView(false)}
					additional={getViewerDetails(url!)}
					canDownload={
						target === OrderTarget.admin ||
						(paymentDocs.includes(url!) &&
							(target === OrderTarget.producer ||
								target === OrderTarget.transporter))
					}
				/>
			) : null}
		</>
	);
};

export default DocumentsTab;
